import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import { DataGrid } from "@mui/x-data-grid";
import { colors } from "../../Shared/colors";
import { Skeleton } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EventIcon from "@mui/icons-material/Event";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import DeviceThermostatIcon from "@mui/icons-material/DeviceThermostat";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useLazyGetLeadTasksQuery } from "../../features/telesaletaskSlice";
import { TaskTable } from "../Telesales/Telesaleleads/Telesalesearchleads/TaskTable";
import { KpiTaskTable } from "./kpiTaskTable";
import moment from 'moment-timezone';
import { usePaginationFromURL } from "../../hooks/usePaginationFromURL";

export const TsKpiDisplay = ({ kpiData, isFetching, startDate, endDate, leadsWorking = [], overdueLeads = [], overdueTasksByType = {} }) => {
  const [taskPopUp, setTaskPopUp] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  // Use URL-based pagination hook (Note: DataGrid uses 0-indexed pages, but we'll convert)
  const { currentPage: urlPage, pageSize, setPage: setUrlPage, setPageSize } = usePaginationFromURL({
    defaultPage: 1,
    defaultPageSize: 10
  });
  const {
    currentPage: overdueUrlPage,
    pageSize: overduePageSize,
    setPage: setOverdueUrlPage,
    setPageSize: setOverduePageSize,
  } = usePaginationFromURL({
    defaultPage: 1,
    defaultPageSize: 10,
    pageParam: "overduePage",
    pageSizeParam: "overduePageSize",
  });
  // Convert to 0-indexed for DataGrid
  const currentPage = urlPage - 1;
  const setCurrentPage = (page) => {
    const pageNum = typeof page === 'function' ? page(currentPage) : page;
    setUrlPage(pageNum >= 0 ? pageNum + 1 : 1);
  };
  const overdueCurrentPage = overdueUrlPage - 1;
  const setOverdueCurrentPage = (page) => {
    const pageNum =
      typeof page === "function" ? page(overdueCurrentPage) : page;
    setOverdueUrlPage(pageNum >= 0 ? pageNum + 1 : 1);
  };
  const [kpiTaskModal, setKpiTaskModal] = useState(false);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [modalTitle, setModalTitle] = useState("");

  useEffect(() => {
    if (taskPopUp) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [taskPopUp]);

  const handleViewTasks = (leadId) => {
    setSelectedLeadId(leadId);
    setTaskPopUp(true);
  };

  // Handle clicking on KPI table cells to show filtered tasks
  const handleKpiCellClick = (agent, metricType) => {
    if (!agent.allTasks || !Array.isArray(agent.allTasks) || agent.allTasks.length === 0) {
      return;
    }

    let filtered = [];
    let title = "";

    const norm = (v) => (typeof v === "string" ? v.trim().toLowerCase() : "");

    switch (metricType) {
      case "tlw":
        // Show all tasks for this agent
        filtered = agent.allTasks;
        title = `${agent.employeeName} - All Tasks (Leads Worked: ${getWorkedLeadsCount(agent)})`;
        break;
      case "callsConnected":
        // Show only "call connected" tasks
        filtered = agent.allTasks.filter(t => {
          const taskType = norm(t.currentTaskType);
          const subType = norm(t.currentSubTypeTask);
          return taskType === "call" && subType === "call connected";
        });
        title = `${agent.employeeName} - Connected Calls (${agent.callsConnected})`;
        break;
      case "callsNotConnected":
        // Show only "call not connected" tasks
        filtered = agent.allTasks.filter(t => {
          const taskType = norm(t.currentTaskType);
          const subType = norm(t.currentSubTypeTask);
          return taskType === "call" && subType === "call not connected";
        });
        title = `${agent.employeeName} - Non-Connected Calls (${agent.callsNotConnected})`;
        break;
      case "uvc":
        // Show unique "call connected" tasks (distinct phone numbers)
        const seenPhones = new Set();
        filtered = agent.allTasks.filter(t => {
          const taskType = norm(t.currentTaskType);
          const subType = norm(t.currentSubTypeTask);
          if (taskType === "call" && subType === "call connected" && t.clientPhone) {
            const phone = String(t.clientPhone).replace(/[\s\-\(\)]/g, '');
            if (!seenPhones.has(phone)) {
              seenPhones.add(phone);
              return true;
            }
          }
          return false;
        });
        title = `${agent.employeeName} - Unique Calls (UVC: ${agent.uniqueCallsCount})`;
        break;
      case "meetings":
        // Show all meeting tasks (not segregated by type)
        filtered = agent.allTasks.filter(t => {
          const taskType = norm(t.currentTaskType);
          return taskType === "meeting";
        });
        title = `${agent.employeeName} - Total Meetings (${agent.meetingsCompleted || 0})`;
        break;
      default:
        return;
    }

    if (filtered.length > 0) {
      setFilteredTasks(filtered);
      setModalTitle(title);
      setKpiTaskModal(true);
    }
  };

  const getWorkedLeadsCount = (agent) => {
    const taskList = Array.isArray(agent?.allTasks) ? agent.allTasks : [];
    const uniqueLeads = new Set(
      taskList
        .map((task) => task?.leadID?.toString?.() || task?.leadID || "")
        .filter(Boolean)
    );
    return uniqueLeads.size;
  };

  // Normalize classification for color coding
  const getClassificationColor = (classification) => {
    const norm = (v) => (typeof v === "string" ? v.trim().toLowerCase() : "");
    const cls = norm(classification);
    if (cls === "veryhot" || cls === "very hot") return colors.veryHot;
    if (cls === "hot") return colors.hot;
    if (cls === "moderate") return colors.moderate;
    if (cls === "cold") return colors.cold;
    return colors.veryCold;
  };

  // DataGrid columns for Leads Working table
  const leadsColumns = [
    {
      field: "client",
      headerName: "Client",
      width: 200,
      flex: 1.2,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.client || "Unknown"} placement="top" arrow>
            <div style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: 600,
              color: "#1e293b",
              fontSize: "0.9rem",
              padding: "8px 0"
            }}>
              {params.row.client || "Unknown"}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "leadId",
      headerName: "Lead ID",
      width: 150,
      renderCell: (params) => {
        const classification = params.row.classification || "moderate";
        const color = getClassificationColor(classification);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <DeviceThermostatIcon sx={{ color: color, fontSize: "1.2rem" }} />
            <span style={{ fontWeight: 600 }}>{params.row.leadId || "-"}</span>
          </div>
        );
      },
    },
    {
      field: "source",
      headerName: "Source",
      width: 120,
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 150,
      renderCell: (params) => {
        const phone = params.row.phone || params.row.cellNo || "";
        if (!phone) return <span style={{ color: "#94a3b8" }}>N/A</span>;
        return (
          <a href={`tel:+${phone}`} style={{ color: colors.brown, textDecoration: "none" }}>
            {phone}
          </a>
        );
      },
    },
    {
      field: "project",
      headerName: "Project",
      width: 150,
      renderCell: (params) => (
        <div style={{ color: colors.textBlue, fontWeight: 500 }}>
          {params.row.project || "N/A"}
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const status = params.row.status || "active";
        const statusColors = {
          assigned: "#3b82f6",
          active: "#10b981",
          reactivated: "#8b5cf6",
          completed: "#6366f1",
          expired: "#ef4444"
        };
        return (
          <span style={{
            padding: "4px 12px",
            borderRadius: "12px",
            fontSize: "0.75rem",
            fontWeight: 600,
            backgroundColor: `${statusColors[status] || "#94a3b8"}20`,
            color: statusColors[status] || "#94a3b8",
            textTransform: "capitalize"
          }}>
            {status}
          </span>
        );
      },
    },
    {
      field: "lastActivity",
      headerName: "Last Activity",
      width: 150,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <ActionCell>
            <Tooltip title="View Tasks" placement="top" arrow>
              <RemoveRedEyeIcon
                style={{ color: colors.brown, cursor: "pointer", fontSize: "1.5rem" }}
                onClick={() => handleViewTasks(params.row.id)}
              />
            </Tooltip>
          </ActionCell>
        );
      },
    },
  ];

  const overdueColumns = [
    { field: "leadID", headerName: "Lead ID", width: 150 },
    { field: "clientName", headerName: "Client", flex: 1, minWidth: 180 },
    { field: "dueDate", headerName: "Due Date", width: 150 },
    { field: "assignedTo", headerName: "Assigned To", flex: 1, minWidth: 180 },
  ];

  // Convert leadsWorking to DataGrid rows
  const leadsRows = leadsWorking.map((lead, index) => ({
    id: lead.id || lead.leadID || `lead-${index}`,
    ...lead
  }));
  const overdueRows = (overdueLeads || []).map((lead, index) => ({
    id: `${lead.leadID || lead.leadId || "overdue"}-${index}`,
    leadID: lead.leadID || lead.leadId || "-",
    clientName: lead.clientName || lead.client || "-",
    dueDate: lead.dueDate || "-",
    assignedTo: lead.assignedTo || "-",
  }));
  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    if (!kpiData || !Array.isArray(kpiData) || kpiData.length === 0) {
      return {
        totalCallsMade: 0,
        totalCallsConnected: 0,
        totalCallsNotConnected: 0,
        totalUVC: 0,
        totalMeetings: 0,
        totalAssignments: 0,
        totalReactivations: 0,
        avgConversionRate: 0,
        avgMeetingConversionRate: 0,
        avgReactivationRate: 0,
        avgResponseTime: 0,
      };
    }

    let totalCallsMade = 0;
    let totalCallsConnected = 0;
    let totalCallsNotConnected = 0;
    let totalUVC = 0;
    let totalMeetings = 0;
    let totalAssignments = 0;
    let totalReactivations = 0;
    let totalResponseTime = 0;
    let agentsWithResponseTime = 0;

    kpiData.forEach((agent) => {
      totalCallsMade += agent.callsMade || 0;
      totalCallsConnected += agent.callsConnected || 0;
      totalCallsNotConnected += agent.callsNotConnected || 0;
      totalUVC += agent.uniqueCallsCount || 0;
      totalMeetings += agent.meetingsCompleted || 0;
      totalAssignments += agent.totalAssignments || 0;
      totalReactivations += agent.leadsReactivated || 0;
      
      if (agent.avgResponseTime && agent.avgResponseTime > 0) {
        totalResponseTime += agent.avgResponseTime;
        agentsWithResponseTime++;
      }
    });

    const avgConversionRate =
      totalCallsMade > 0
        ? ((totalCallsConnected / totalCallsMade) * 100).toFixed(2)
        : 0;

    const avgMeetingConversionRate =
      totalCallsConnected > 0
        ? ((totalMeetings / totalCallsConnected) * 100).toFixed(2)
        : 0;

    const avgReactivationRate =
      totalAssignments > 0
        ? ((totalReactivations / totalAssignments) * 100).toFixed(2)
        : 0;

    const avgResponseTime =
      agentsWithResponseTime > 0
        ? (totalResponseTime / agentsWithResponseTime).toFixed(1)
        : 0;

    return {
      totalCallsMade,
      totalCallsConnected,
      totalCallsNotConnected,
      totalUVC,
      totalMeetings,
      totalAssignments,
      totalReactivations,
      avgConversionRate: parseFloat(avgConversionRate),
      avgMeetingConversionRate: parseFloat(avgMeetingConversionRate),
      avgReactivationRate: parseFloat(avgReactivationRate),
      avgResponseTime: parseFloat(avgResponseTime),
    };
  }, [kpiData]);

  // Prepare overdue tasks by type data
  const overdueTasksData = useMemo(() => {
    if (!overdueTasksByType || Object.keys(overdueTasksByType).length === 0) {
      return [];
    }

    return [
      { 
        name: "Follow-up", 
        value: overdueTasksByType.followUp || 0, 
        color: "#ef4444" 
      },
      { 
        name: "Meeting Schedule", 
        value: overdueTasksByType.meeting_schedule || 0, 
        color: "#f59e0b" 
      },
      { 
        name: "Meeting Reschedule", 
        value: overdueTasksByType.meeting_reschedule || 0, 
        color: "#8b5cf6" 
      },
    ].filter((item) => item.value > 0);
  }, [overdueTasksByType]);

  // Prepare agent performance data for bar chart
  const agentPerformanceData = useMemo(() => {
    if (!kpiData || !Array.isArray(kpiData)) return [];

    return kpiData.map((agent) => ({
      name: agent.employeeName?.split(" ")[0] || "Agent",
      conversionRate: agent.conversionRate || 0,
      meetingConversionRate: agent.meetingConversionRate || 0,
      reactivationRate: agent.reactivationRate || 0,
    }));
  }, [kpiData]);

  const summaryCards = [
    {
      title: "Calls Made",
      value: summaryMetrics.totalCallsMade,
      icon: PhoneIcon,
      color: "#3b82f6",
      bgColor: "#dbeafe",
    },
    {
      title: "Calls Connected",
      value: summaryMetrics.totalCallsConnected,
      icon: PhoneCallbackIcon,
      color: "#10b981",
      bgColor: "#d1fae5",
    },
    {
      title: "Calls Not Connected",
      value: summaryMetrics.totalCallsNotConnected,
      icon: PhoneDisabledIcon,
      color: "#ef4444",
      bgColor: "#fee2e2",
    },
    {
      title: "Unique Calls (UVC)",
      value: summaryMetrics.totalUVC,
      icon: PhoneCallbackIcon,
      color: "#8b5cf6",
      bgColor: "#ede9fe",
    },
    {
      title: "Conversion Rate",
      value: `${summaryMetrics.avgConversionRate}%`,
      icon: TrendingUpIcon,
      color: "#f59e0b",
      bgColor: "#fef3c7",
    },
    {
      title: "Meeting Conversion",
      value: `${summaryMetrics.avgMeetingConversionRate}%`,
      icon: EventIcon,
      color: "#06b6d4",
      bgColor: "#cffafe",
    },
    {
      title: "Reactivation Rate",
      value: `${summaryMetrics.avgReactivationRate}%`,
      icon: PersonAddIcon,
      color: "#10b981",
      bgColor: "#d1fae5",
    },
    {
      title: "Avg Response Time",
      value: `${summaryMetrics.avgResponseTime}h`,
      icon: AccessTimeIcon,
      color: "#6366f1",
      bgColor: "#e0e7ff",
    },
  ];

  return (
    <Container>
      {/* Summary Cards Section */}
      <Section>
        <SectionHeader>
          <h2>TS Activity Summary</h2>
          <p>
            {startDate && endDate
              ? `${startDate} to ${endDate}`
              : "Select date range to view KPIs"}
          </p>
        </SectionHeader>

        {isFetching ? (
          <CardsGrid>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Card key={i}>
                <Skeleton variant="rectangular" height={120} />
              </Card>
            ))}
          </CardsGrid>
        ) : (
          <CardsGrid>
            {summaryCards.map((card, index) => (
              <KPICard key={index}>
                <CardHeader>
                  <IconContainer bgColor={card.bgColor}>
                    <card.icon style={{ color: card.color, fontSize: "1.5rem" }} />
                  </IconContainer>
                </CardHeader>
                <CardContent>
                  <CardTitle>{card.title}</CardTitle>
                  <CardValue>{card.value}</CardValue>
                </CardContent>
              </KPICard>
            ))}
          </CardsGrid>
        )}
      </Section>

      {/* Charts Section */}
      {!isFetching && kpiData && kpiData.length > 0 && (
        <ChartsSection>
          <ChartsGrid>
            {/* Overdue Tasks by Type */}
            <ChartCard>
              <ChartTitle>Overdue Tasks by Type</ChartTitle>
              {overdueTasksData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={overdueTasksData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) =>
                        `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {overdueTasksData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value, name) => [`${value} tasks`, name]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <NoDataMessage>No overdue tasks in the selected date range</NoDataMessage>
              )}
            </ChartCard>

            {/* Agent Performance Comparison */}
            <ChartCard>
              <ChartTitle>Agent Performance Comparison</ChartTitle>
              {agentPerformanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={agentPerformanceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="conversionRate" fill="#3b82f6" name="Conversion %" />
                    <Bar
                      dataKey="meetingConversionRate"
                      fill="#8b5cf6"
                      name="Meeting Conversion %"
                    />
                    <Bar
                      dataKey="reactivationRate"
                      fill="#10b981"
                      name="Reactivation %"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <NoDataMessage>No agent performance data available</NoDataMessage>
              )}
            </ChartCard>
          </ChartsGrid>
        </ChartsSection>
      )}

      {/* Agent Performance Summary Table - Matching KPI Report Structure */}
      {!isFetching && kpiData && kpiData.length > 0 && (
        <TableSection>
          <SectionHeader>
            <h2>Agent Performance Summary</h2>
          </SectionHeader>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th></th>
                  <th>Lead</th>
                  <th colSpan={4}>Calls</th>
                  <th>Meeting</th>
                  <th colSpan={2}>Average</th>
                </tr>
                <tr>
                  <th>Name</th>
                  <th>TLW</th>
                  <th>Whatsapp</th>
                  <th>Con. Calls</th>
                  <th>Non Con. Calls</th>
                  <th>UVC</th>
                  <th>Total Meetings</th>
                  <th>Avg Calls/Day</th>
                  <th>Avg Meeting/Day</th>
                </tr>
              </thead>
              <tbody>
                {kpiData.map((agent, index) => (
                  <tr key={index}>
                    <td style={{ color: `${colors.brown}`, fontWeight: "600" }}>
                      {agent.employeeName || "-"}
                    </td>
                    <ClickableCell
                      onClick={() => handleKpiCellClick(agent, "tlw")}
                      style={{ color: `${colors.brown}`, fontWeight: "600" }}
                    >
                      {getWorkedLeadsCount(agent)}
                    </ClickableCell>
                    <td>{agent.whatsappCount || 0}</td>
                    <ClickableCell
                      onClick={() => handleKpiCellClick(agent, "callsConnected")}
                      style={{ color: `${colors.brown}`, fontWeight: "600" }}
                    >
                      {agent.callsConnected || 0}
                    </ClickableCell>
                    <ClickableCell
                      onClick={() => handleKpiCellClick(agent, "callsNotConnected")}
                      style={{ color: `${colors.brown}`, fontWeight: "600" }}
                    >
                      {agent.callsNotConnected || 0}
                    </ClickableCell>
                    <ClickableCell
                      onClick={() => handleKpiCellClick(agent, "uvc")}
                      style={{ color: `${colors.brown}`, fontWeight: "bold" }}
                    >
                      {agent.uniqueCallsCount || 0}
                    </ClickableCell>
                    <ClickableCell
                      onClick={() => handleKpiCellClick(agent, "meetings")}
                      style={{ color: `${colors.brown}`, fontWeight: "600" }}
                    >
                      {agent.meetingsCompleted || 0}
                    </ClickableCell>
                    <td>{agent.avgCallsPerDay || agent.avgCallPerDay || "-"}</td>
                    <td>{agent.avgMeetingsPerDay || agent.avgMeetingPerDay || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </TableSection>
      )}

      {/* Leads Working Table - DataGrid similar to Search Leads */}
      {!isFetching && leadsWorking && leadsWorking.length > 0 && (
        <TableSection>
          <SectionHeader>
            <h2>Leads Working</h2>
            <p>Active leads with recent activity in the selected date range</p>
          </SectionHeader>
          <DataGridContainer>
            <DataGrid
              rows={leadsRows}
              columns={leadsColumns}
              pageSize={pageSize}
              page={currentPage}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setCurrentPage(0); // Reset to first page when page size changes
              }}
              onPageChange={(newPage) => setCurrentPage(newPage)}
              rowsPerPageOptions={[10, 25, 50, 100]}
              disableSelectionOnClick
              autoHeight={false}
              pagination
              sx={{
                border: "none",
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                "& .MuiDataGrid-root": {
                  border: "none",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: colors.brown,
                  color: colors.white,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 600,
                  },
                },
                "& .MuiDataGrid-row": {
                  "&:hover": {
                    backgroundColor: "#f8fafc",
                  },
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #e2e8f0",
                  fontSize: "0.875rem",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "2px solid #e2e8f0",
                  backgroundColor: "#f8fafc",
                },
              }}
            />
          </DataGridContainer>
        </TableSection>
      )}

      {/* Overdue Leads Table - DataGrid with pagination */}
      {!isFetching && overdueRows.length > 0 && (
        <TableSection>
          <SectionHeader>
            <h2>Overdue Leads</h2>
            <p>Leads with overdue next tasks</p>
          </SectionHeader>
          <DataGridContainer>
            <DataGrid
              rows={overdueRows}
              columns={overdueColumns}
              pageSize={overduePageSize}
              page={overdueCurrentPage}
              onPageSizeChange={(newPageSize) => {
                setOverduePageSize(newPageSize);
                setOverdueCurrentPage(0);
              }}
              onPageChange={(newPage) => setOverdueCurrentPage(newPage)}
              rowsPerPageOptions={[10, 25, 50, 100]}
              disableSelectionOnClick
              autoHeight={false}
              pagination
              sx={{
                border: "none",
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: colors.brown,
                  color: colors.white,
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f8fafc",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #e2e8f0",
                  fontSize: "0.875rem",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "2px solid #e2e8f0",
                  backgroundColor: "#f8fafc",
                },
              }}
            />
          </DataGridContainer>
        </TableSection>
      )}

      {/* Task View Modal */}
      {taskPopUp && selectedLeadId && (
        <PopUpContainer popUp={taskPopUp}>
          <PopUp>
            <Cross onClick={() => setTaskPopUp(false)}>
              <img src="/assets/icons/cross.png" alt="close" />
            </Cross>
            <PopUpSection>
              <TaskTable leadId={selectedLeadId} />
            </PopUpSection>
          </PopUp>
        </PopUpContainer>
      )}

      {/* KPI Task Filter Modal */}
      {kpiTaskModal && (
        <PopUpContainer popUp={kpiTaskModal}>
          <PopUp>
            <Cross onClick={() => setKpiTaskModal(false)}>
              <img src="/assets/icons/cross.png" alt="close" />
            </Cross>
            <PopUpSection>
              <ModalHeader>
                <h2>{modalTitle}</h2>
                <p>Total Tasks: {filteredTasks.length}</p>
              </ModalHeader>
              <KpiTaskTable data={filteredTasks} />
            </PopUpSection>
          </PopUp>
        </PopUpContainer>
      )}

    </Container>
  );
};

// Styled Components
const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 1rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionHeader = styled.div`
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${colors.text};
    margin: 0 0 0.5rem 0;
  }

  p {
    color: #64748b;
    margin: 0;
    font-size: 0.95rem;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const KPICard = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  padding: 2rem 1.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem;
    min-height: 140px;
  }

  @media (max-width: 480px) {
    padding: 1.25rem 1rem;
    min-height: 130px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`;

const IconContainer = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 10px;
  background: ${(props) => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CardTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CardValue = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: ${colors.text};
  margin: 0;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const ChartsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const ChartTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${colors.text};
  margin: 0 0 1rem 0;
  text-align: center;
`;

const NoDataMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 250px;
  color: #64748b;
  font-size: 0.9rem;
  font-style: italic;
`;

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  -webkit-overflow-scrolling: touch;

  /* Scrollbar styling for better UX */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.brown};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #1a6b6b;
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 1200px;
  border-collapse: collapse;
  background: ${colors.white};
  border: 1px solid #e2e8f0;

  @media (max-width: 1400px) {
    min-width: 1100px;
  }

  @media (max-width: 768px) {
    min-width: 1000px;
  }

  thead {
    background-color: ${colors.brown};
    color: ${colors.white};

    tr:first-child {
      th {
        padding: 10px 12px;
        text-align: center;
        font-weight: 700;
        font-size: 0.9rem;
        border-right: 1px solid rgba(255, 255, 255, 0.2);

        &:last-child {
          border-right: none;
        }
      }
    }

    tr:last-child {
      th {
        padding: 10px 12px;
        text-align: center;
        font-weight: 600;
        font-size: 0.85rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        white-space: nowrap;
        border-right: 1px solid rgba(255, 255, 255, 0.2);

        &:last-child {
          border-right: none;
        }

        @media (max-width: 768px) {
          padding: 8px 10px;
          font-size: 0.7rem;
        }
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #e2e8f0;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: #f8fafc;
      }

      &:last-child {
        border-bottom: none;
      }
    }

    td {
      padding: 10px 12px;
      color: ${colors.text};
      font-size: 0.9rem;
      text-align: center;
      border-right: 1px solid #e2e8f0;

      &:last-child {
        border-right: none;
      }

      @media (max-width: 768px) {
        padding: 8px 10px;
        font-size: 0.8rem;
      }
    }
  }
`;

const PercentageCell = styled.span`
  font-weight: 600;
  color: ${(props) => {
    if (props.value >= props.threshold) return "#10b981";
    if (props.value >= props.threshold * 0.5) return "#f59e0b";
    return "#ef4444";
  }};
`;

const DataGridContainer = styled.div`
  width: 100%;
  height: 600px;
  background: ${colors.white};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    height: 500px;
  }
`;

const ActionCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 8px;
`;

const PopUpContainer = styled.div`
  background-color: #0000004d;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: ${(props) => (props.popUp ? "flex" : "none")};
  justify-content: center;
  align-items: center;
`;

const PopUp = styled.div`
  width: 95vw;
  max-width: 1200px;
  height: 85vh;
  max-height: 800px;
  overflow: hidden;
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  position: relative;
`;

const Cross = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #ff4757;
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(255, 71, 87, 0.3);
  
  &:hover {
    background: #ff3742;
    box-shadow: 0 6px 16px rgba(255, 71, 87, 0.4);
  }
  
  img {
    width: 20px;
    height: 20px;
    filter: brightness(0) invert(1);
  }
`;

const PopUpSection = styled.div`
  height: 100%;
  width: 100%;
  padding: 60px 20px 20px 20px;
  overflow: auto;
`;

const Card = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ClickableCell = styled.td`
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background-color: #e0e7ff !important;
    transform: scale(1.05);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ModalHeader = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e2e8f0;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${colors.text};
    margin: 0 0 0.5rem 0;
  }

  p {
    color: #64748b;
    margin: 0;
    font-size: 0.95rem;
  }
`;

